// Section for import npm components -----------------------------------------------------------------------------------
import React, { useState, useContext } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';

// MUI components
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import FormControl from '@mui/material/FormControl';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';

// MUI icons
import SearchIcon from '@mui/icons-material/Search';

// Section for import custom components --------------------------------------------------------------------------------
import { AppConfigContext } from '@providers/AppConfigProvider';

// Type definitions ----------------------------------------------------------------------------------------------------
interface MansionSearchFormInput {
  searchText: string;
}

type MansionSearchValidateErrorProps = {
  message: string | undefined;
};

const searchUrlPrefix: string = 'search/keyword/?keyword=';
const initComposition: boolean = false;

function MansionSearchValidateError(props: MansionSearchValidateErrorProps): React.JSX.Element {
  const { message } = props;
  return (
    <Box className="my-2">
      <Alert severity="error">
        <Typography>{message}</Typography>
      </Alert>
    </Box>
  );
}

// Section for render component ----------------------------------------------------------------------------------------
export default function MansionSearch() {
  const { isMobile, pathRoot } = useContext(AppConfigContext);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<MansionSearchFormInput>();

  const { name, ref, onChange, onBlur } = register('searchText', {
    required: '検索文字列を入力してください。',
    minLength: { value: 2, message: '検索文字は2文字以上で入力してください。' },
  });

  const [compositionState, setComposition] = useState(initComposition);
  const startComposition = () => setComposition(true);
  const endComposition = () => setComposition(false);

  const onSubmit: SubmitHandler<MansionSearchFormInput> = (data) => {
    const encodeSearchText: string = encodeURI(data.searchText);
    window.location.href = `${pathRoot}${searchUrlPrefix}${encodeSearchText}`;
  };

  const onKeyDown = (_event: React.KeyboardEvent<HTMLDivElement>, key: string) => {
    switch (key) {
      case 'Enter':
        if (compositionState) break;
        handleSubmit(onSubmit);
        // console.log(event.key);
        break;
      default:
        break;
    }
  };

  return (
    <Container className="text-center md:my-16 max-md:mt-10 max-md:mb-12 px-0 max-md:px-5">
      <Typography className="max-sm:block sm:hidden text-lg font-bold leading-6 tracking-normal text-left mb-4">
        キーワードから探す
      </Typography>

      <FormControl className="sm:max-lg:w-full max-sm:block">
        <form
          onSubmit={
            compositionState
              ? (event) => {
                  event.preventDefault();
                }
              : handleSubmit(onSubmit)
          }
        >
          {isMobile ? (
            <Box className="block relative">
              <Box className="relative">
                <SearchIcon className="w-4 h-4 text-[#A2A2A2] absolute -translate-y-2/4 left-4 top-2/4" />
                <TextField
                  id="searchText"
                  label="地名、駅名、マンション名など"
                  type="text"
                  className="w-full mb-0"
                  name={name}
                  onCompositionStart={startComposition}
                  onCompositionEnd={endComposition}
                  onKeyDown={(event) => onKeyDown(event, event.key)}
                  onChange={onChange}
                  onBlur={onBlur}
                  ref={ref}
                  error={!!errors.searchText}
                />
              </Box>
              {errors.searchText && <MansionSearchValidateError message={errors.searchText.message} />}
              <Button
                color="primary"
                variant="contained"
                size="large"
                type="submit"
                className="group p-0 shadow-none w-full mt-4"
              >
                <Box className="rf-btn-sm-sp-max">検索する</Box>
                <ArrowForwardRoundedIcon className="text-[#191919] max-sm:block w-5 absolute right-4 -translate-y-2/4 top-2/4" />
              </Button>
            </Box>
          ) : (
            <Box className="sm:max-lg:w-[calc(100%-192px)] md:max-lg:mx-auto">
              <Box className="items-center flex relative gap-4">
                <SearchIcon className="w-4 h-4 text-[#A2A2A2] absolute -translate-y-2/4 left-4 top-2/4" />
                <TextField
                  id="searchText"
                  label="地名、駅名、マンション名など"
                  type="text"
                  className="w-[440px] sm:max-lg:w-full mb-0 search-field"
                  name={name}
                  onCompositionStart={startComposition}
                  onCompositionEnd={endComposition}
                  onKeyDown={(event) => onKeyDown(event, event.key)}
                  onChange={onChange}
                  onBlur={onBlur}
                  ref={ref}
                  error={!!errors.searchText}
                />

                <Button
                  color="primary"
                  variant="contained"
                  size="large"
                  type="submit"
                  className="group p-0 shadow-none"
                >
                  <Box className="rf-btn-sm-sp-max">検索する</Box>
                  <img
                    src={`${pathRoot}/assets/images/common/ArrowForwardRoundedIcon_black.svg`}
                    alt="arrow"
                    className="opacity-0  group-hover:opacity-100 absolute top-[18px] right-[12px] w-5 transition delay-150"
                  />
                </Button>
              </Box>
              {errors.searchText && <MansionSearchValidateError message={errors.searchText.message} />}
            </Box>
          )}
        </form>
      </FormControl>
    </Container>
  );
}
